import React, { useState } from 'react';
import { Box, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';

// Palette of the billing app (CashMovementModal / react-dev-billing tokens).
export const P = {
  brand: '#4F46E5', brandHover: '#4338CA', brandBg: '#EEF2FF', brandBorder: '#C7D2FE',
  border: '#E2E8F0', divider: '#F1F5F9', surface: '#F8FAFC',
  text: '#1E293B', text2: '#64748B', muted: '#94A3B8', disabled: '#CBD5E1', secondary: '#475569',
  success: '#16A34A', successBg: '#F0FDF4', successBorder: '#BBF7D0',
  danger: '#DC2626', dangerIcon: '#EF4444', dangerBg: '#FEF2F2', dangerBorder: '#FECACA',
  warnBg: '#FFFBEB', warnText: '#92400E', warnBorder: '#FDE68A',
  infoBg: '#EEF2FF', infoText: '#3730A3',
};

export const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: P.surface, borderRadius: '8px', fontSize: '0.9rem',
    '& fieldset': { borderColor: P.border },
    '&:hover fieldset': { borderColor: P.disabled },
    '&.Mui-focused fieldset': { borderColor: P.brand },
  },
  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: P.brand },
  '& .MuiFormHelperText-root': { fontSize: '0.7rem', mx: 0.5 },
};

export const money = (v) => {
  const n = Number(v || 0);
  const s = Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `−₹${s}` : `₹${s}`;
};
export const num = (v) => (v === '' || v === null || v === undefined ? NaN : Number(v));
export const isAmount = (v) => Number.isFinite(num(v)) && num(v) > 0;
// Two decimals at most, digits only — the CashMovementModal rule.
export const amountInput = (v) => (/^\d*\.?\d{0,2}$/.test(v) ? v : null);

const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : 8 + (r % 4)).toString(16);
  }));

// One idempotency key per form (forms remount per action); kept across retries (offline, timeout) and renewed
// after a success.
export function useClientRef() {
  const [ref, setRef] = useState(uuid);
  return [ref, () => setRef(uuid())];
}

const NOTE = {
  ok: [P.successBg, P.successBorder, '#166534'], wa: [P.warnBg, P.warnBorder, P.warnText],
  er: [P.dangerBg, P.dangerBorder, '#991B1B'], in: [P.infoBg, P.brandBorder, P.infoText],
};
export function Note({ tone = 'in', children }) {
  const [bg, border, fg] = NOTE[tone];
  return (
    <Box role={tone === 'er' ? 'alert' : undefined} sx={{ bgcolor: bg, border: `1px solid ${border}`, color: fg, borderRadius: '8px', px: 1.5, py: 1, fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.45 }}>
      {children}
    </Box>
  );
}

export function Label({ children }) {
  return <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: P.text2, mb: 1 }}>{children}</Typography>;
}

// The big ₹ amount box (52px, decimal keypad).
export function AmountField({ value, onChange, error, label = 'Amount', disabled, autoFocus }) {
  return (
    <TextField fullWidth required label={label} value={value} disabled={disabled} autoFocus={autoFocus}
      onChange={(e) => { const v = amountInput(e.target.value); if (v !== null) onChange(v); }}
      error={!!error} helperText={error || ' '} inputProps={{ inputMode: 'decimal', 'aria-label': label }}
      InputProps={{
        startAdornment: <InputAdornment position="start"><Typography sx={{ fontWeight: 700, color: P.text2, fontSize: '1rem' }}>₹</Typography></InputAdornment>,
      }}
      sx={{ ...fieldSx, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], height: 52, fontSize: '1.1rem', fontWeight: 700 } }} />
  );
}

export function Field({ label, value, onChange, required, error, helper, select, options, placeholder, disabled, inputMode, children }) {
  return (
    <TextField fullWidth size="small" label={label} required={required} value={value ?? ''} disabled={disabled} select={select}
      onChange={(e) => onChange(e.target.value)} error={!!error} helperText={error || helper || ' '} placeholder={placeholder}
      inputProps={{ inputMode }} sx={fieldSx}>
      {select ? (options || []).map((o) => <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.88rem' }}>{o.label}</MenuItem>) : children}
    </TextField>
  );
}

// 422 codes that belong under the amount box / the bill fields; anything else is a note above the buttons.
export const AMOUNT_CODES = ['insufficient_cash', 'over_voucher_limit', 'cash_limit', 'nothing_to_top_up'];
export const BILL_CODES = ['bill_required', 'duplicate_bill', 'bad_bill', 'too_many_bills'];
export const errMessage = (err, fallback) => err?.data?.error || (err?.status === 'FETCH_ERROR' ? "Couldn't reach the server. Tap again to retry; it won't be recorded twice." : fallback);
